import type { ElementFileUploadEvent } from '@coorum-research/shared-common/types'
import type { SurveyAnswerRecord } from '@coorum-research/shared-common/types/answer.type'
import type { SurveyConfig } from '@coorum-research/shared-common/types/survey-json.type'
import type { ThemeConfig } from '@coorum-research/shared-common/types/theme.type'
import type { ValidateQuestionEvent } from 'survey-core'
import { validateTextInput } from '@coorum-research/shared-common/shared/survey/survey-validation'
import { useCallback } from 'react'
import { Serializer, surveyLocalization, SurveyModel } from 'survey-core'
import { coorumCss } from '../survey/coorumCss'
import { coorumTheme } from '../survey/coorumTheme'
import { initSurveySettings } from '../survey/survey-model.util'
import { createSurveyTheme } from '../survey/theme'
import 'survey-core/survey-core.min.css'

// 評価をrenderingするときにdropdownにならないよう、defaultValueをauto→buttonsにする
const ratingDisplayModeProp = Serializer.findProperty('rating', 'displayMode')
ratingDisplayModeProp.defaultValue = 'buttons'

type Props = {
  themeJson?: ThemeConfig | null
  mode?: 'display' | 'edit' // TODO: survey-coreに型があるならそれを使う
  onUploadFiles?: (option: ElementFileUploadEvent) => void
}

/**
 * NOTE: useSurveyModelと統合することも考えたが、
 * useSurveyModelはSurveyAnswerList.tsxで使うので統合はしない
 */
export function useSurveyModelPerQuestion({
  themeJson,
  mode,
}: Props) {
  initSurveySettings(Serializer, surveyLocalization)

  const getModel = useCallback((perElementSurveyConfig: SurveyConfig, perElementResultData: SurveyAnswerRecord | null) => {
    const model = new SurveyModel(perElementSurveyConfig)
    model.locale = 'ja'

    // モードの設定(display = 閲覧のみ, edit = 回答可能)
    model.mode = mode ?? 'edit'

    // 質問ごとにページを分ける
    model.questionsOnPageMode = 'questionPerPage'

    // 質問番号は非表示
    model.showQuestionNumbers = false

    // for分岐 - navigation buttonを隠す
    model.showNavigationButtons = 'none'

    // for 分岐 - 進捗バーの表示を隠す
    model.showProgressBar = 'off'

    // ProgressText(Page 1 of n)は表示しない
    model.onProgressText.add((_sender, options) => {
      options.text = ''
    })

    // 完了ページの表示有無
    model.showCompletedPage = false

    if (themeJson) {
      model.applyTheme(createSurveyTheme(themeJson))
    } else {
      model.applyTheme(coorumTheme)
    }
    model.css = coorumCss

    // imagepickerのshowCaptionで、showLabelを制御する
    if (perElementSurveyConfig.elements && perElementSurveyConfig.elements.length > 0 && perElementSurveyConfig.elements[0].type === 'imagepicker') {
      const question = perElementSurveyConfig.elements[0]
      if (!question.showCaption) {
        model.css.imagepicker.itemText = 'hidden'
      }
    }

    if (model.mode === 'display') {
      model.css.rating.itemStarDisabled = ''
      model.css.rating.itemStarReadOnly = ''
      model.css.rating.itemDisabled = ''
      model.css.rating.itemReadOnly = ''
      model.css.imagepicker.itemReadOnly = ''
      model.css.file.fileDecorator = 'sv-hidden'
      model.css.file.imageWrapper = `${model.css.file.imageWrapper} !bg-transparent`
    }

    if (perElementResultData) {
      // 1つ elementのデータがあれば default値としてセットする。
      model.data = perElementResultData
    }

    // フリーテキスト（単語・文章）のバリデーション
    model.onValidateQuestion.add((_sender: SurveyModel, options: ValidateQuestionEvent) => {
      const question = options.question
      const errors = validateTextInput(
        options.value,
        question.getType(),
        question.getPropertyValue('validationType') || 'none',
      )

      if (errors.length > 0) {
        // エラーメッセージをSurveyModelのエラーとして設定
        const error = errors[0]
        question.errors = [error] // 質問のエラーを直接設定
        options.error = error.getText() // これがquestion.errorsに反映される
      } else {
        question.errors = [] // エラーをクリア
      }
    })

    return model
  }, [themeJson, mode])

  return {
    getModel,
  }
}
